import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { Badge, Switch } from '@/components/ui/misc';
import { entityKindOf } from '@/lib/entities';
import { isoToWall, wallToIso } from '@/lib/format';
import { useI18n } from '@/lib/i18n';
import { deref, type JsonSchema } from '@/lib/openapi';
import { cn } from '@/lib/utils';
import { EntityPicker, MediaUpload } from './pickers';

export type FormValues = Record<string, unknown>;

export interface FieldOverride {
  label?: string;
  hint?: string;
  hidden?: boolean;
  /** Replace the widget entirely. */
  render?(value: unknown, onChange: (v: unknown) => void): ReactNode;
  full?: boolean;
}

export interface SchemaFormProps {
  schema: JsonSchema;
  value: FormValues;
  onChange(value: FormValues): void;
  errors?: Record<string, string>;
  /** Screen context (e.g. `admin/boarding-contracts`) to resolve ambiguous id fields. */
  context?: string;
  overrides?: Record<string, FieldOverride>;
  /** Fields the user cannot change (shown read-only). */
  readOnly?: string[];
  columns?: 1 | 2;
}

const DATE_ONLY_KEYS = /(dateOfBirth|^date$|Date$|^validFrom$|^validUntil$|arrivedAt$|leftAt$|fromDate$|toDate$|startDate$|endDate$|requestedFor$|occurrenceDate$)/;
const LONG_TEXT_KEYS = /(body|description|notes|message|answer|bio|comment|reply|reason|excerpt|handlingNotes|tackingQuirks|likes|dislikes|experienceNotes|medicalNotes|content|address)/i;
const MONEY_KEY = /(Amount|^price|^value$)$/;
const DATETIME_KEYS = /(At|^windowFrom|^windowTo|Deadline|^from|^to|^now)$/;
const TIME_KEYS = /^(startTime|time|open|close|from|to)$/;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Build initial form values from a schema (defaults) merged with an existing record (for edits). */
export function initialValues(schema: JsonSchema, record?: Record<string, unknown> | null): FormValues {
  const out: FormValues = {};
  for (const [key, raw] of Object.entries(schema.properties ?? {})) {
    const s = deref(raw);
    if (record && key in record && record[key] !== undefined) out[key] = record[key];
    else if (s.default !== undefined) out[key] = s.default;
  }
  return out;
}

/** Drop empty optional values and coerce types before sending to the API. */
export function cleanValues(schema: JsonSchema, values: FormValues): FormValues {
  const out: FormValues = {};
  const required = new Set(schema.required ?? []);
  for (const [key, raw] of Object.entries(schema.properties ?? {})) {
    const s = deref(raw);
    let v = values[key];
    if (v === undefined || v === '' || (v === null && !required.has(key))) continue;
    if (s.type === 'number' && typeof v === 'string') v = Number(v);
    if (s.type === 'object' && s.properties && v && typeof v === 'object' && !Array.isArray(v)) v = cleanValues(s, v as FormValues);
    if (s.type === 'array' && Array.isArray(v) && deref(s.items).properties) v = (v as FormValues[]).map((item) => cleanValues(deref(s.items), item));
    out[key] = v;
  }
  return out;
}

/** Client-side checks mirroring the API's class-validator rules (the API stays the source of truth). */
export function validate(schema: JsonSchema, values: FormValues, label: (k: string) => string): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const key of schema.required ?? []) {
    const v = values[key];
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0 && (deref(schema.properties?.[key]).minItems ?? 0) > 0)) errs[key] = `${label(key)} is required`;
  }
  for (const [key, raw] of Object.entries(schema.properties ?? {})) {
    const s = deref(raw);
    const v = values[key];
    if (v === undefined || v === null || v === '' || errs[key]) continue;
    if (typeof v === 'string' && s.minLength && v.length < s.minLength) errs[key] = `At least ${s.minLength} characters`;
    if (typeof v === 'string' && s.maxLength && v.length > s.maxLength) errs[key] = `At most ${s.maxLength} characters`;
    if (typeof v === 'number' && s.minimum !== undefined && v < s.minimum) errs[key] = `Must be ≥ ${s.minimum}`;
    if (typeof v === 'number' && s.maximum !== undefined && v > s.maximum) errs[key] = `Must be ≤ ${s.maximum}`;
    if (typeof v === 'string' && s.pattern && !new RegExp(s.pattern, 'i').test(v)) errs[key] = 'Invalid format';
    if (s.format === 'email' && typeof v === 'string' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) errs[key] = 'Invalid e-mail';
  }
  return errs;
}

export function SchemaForm({ schema, value, onChange, errors = {}, context = '', overrides = {}, readOnly = [], columns = 2 }: SchemaFormProps) {
  const { label } = useI18n();
  const required = new Set(schema.required ?? []);
  const entries = Object.entries(schema.properties ?? {}).filter(([k]) => !overrides[k]?.hidden);
  return (
    <div className={cn('grid gap-x-5 gap-y-4', columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
      {entries.map(([key, raw]) => {
        const s = deref(raw);
        const ov = overrides[key] ?? {};
        const full = ov.full || isFullWidth(key, s);
        const set = (v: unknown) => onChange({ ...value, [key]: v });
        const disabled = readOnly.includes(key);
        return (
          <Field
            key={key}
            className={cn(full && 'sm:col-span-2')}
            label={ov.label ?? label(key)}
            required={required.has(key)}
            hint={ov.hint ?? hintFor(key, s)}
            error={errors[key]}
          >
            {ov.render ? ov.render(value[key], set) : <Widget k={key} s={s} v={value[key]} onChange={set} context={context} invalid={Boolean(errors[key])} disabled={disabled} />}
          </Field>
        );
      })}
    </div>
  );
}

function isFullWidth(key: string, s: JsonSchema): boolean {
  if (s.type === 'array' && deref(s.items).properties) return true;
  if (s.type === 'object') return true;
  if (s.type === 'string' && ((s.maxLength ?? 0) > 300 || LONG_TEXT_KEYS.test(key))) return true;
  return false;
}

function hintFor(key: string, s: JsonSchema): string | undefined {
  if (s.description && s.description.length < 140) return s.description;
  if (MONEY_KEY.test(key) && s.type === 'number') return 'Amount in the stable currency';
  if (key === 'rrule') return 'e.g. FREQ=WEEKLY;BYDAY=TU,TH';
  return undefined;
}

function Widget({ k, s, v, onChange, context, invalid, disabled }: { k: string; s: JsonSchema; v: unknown; onChange(v: unknown): void; context: string; invalid?: boolean; disabled?: boolean }) {
  const kind = s.type === 'string' || (s.type === 'array' && deref(s.items).type === 'string') ? entityKindOf(k, context) : null;

  // Files
  if (/MediaId$|^mediaId$/.test(k) && s.type === 'string') {
    return <MediaUpload value={v as string} onChange={onChange} accept={/signature|pdf|attachment/i.test(k) ? '*/*' : 'image/*'} />;
  }
  // Records
  if (kind && s.type === 'string') return <EntityPicker kind={kind} value={v as string} onChange={onChange} invalid={invalid} />;
  if (kind && s.type === 'array') return <EntityPicker kind={kind} value={(v as string[]) ?? []} onChange={onChange} multiple />;

  if (s.enum) {
    return (
      <Select value={v === undefined || v === null ? '' : String(v)} onChange={(e) => onChange(e.target.value === '' ? undefined : typeof s.enum![0] === 'number' ? Number(e.target.value) : e.target.value)} aria-invalid={invalid} disabled={disabled}>
        <option value="">—</option>
        {s.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt).replace(/_/g, ' ')}
          </option>
        ))}
      </Select>
    );
  }

  switch (s.type) {
    case 'boolean':
      return (
        <div className="flex h-9 items-center">
          <Switch checked={Boolean(v)} onCheckedChange={onChange} disabled={disabled} />
        </div>
      );
    case 'number':
    case 'integer':
      if (MONEY_KEY.test(k) && !/Pct|Count/i.test(k)) return <MoneyInput value={v as number | undefined} onChange={onChange} invalid={invalid} disabled={disabled} />;
      return <Input type="number" value={v === undefined || v === null ? '' : String(v)} min={s.minimum} max={s.maximum} step={/Hh|Kg|Pct|qty|Qty/.test(k) ? 'any' : 1} onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} aria-invalid={invalid} disabled={disabled} />;
    case 'array':
      return <ArrayWidget k={k} s={s} v={v as unknown[] | undefined} onChange={onChange} context={context} />;
    case 'object':
      if (s.properties) return <NestedObject s={s} v={(v as FormValues) ?? {}} onChange={onChange} context={context} />;
      return <JsonEditor value={v} onChange={onChange} />;
    default: {
      const str = v === undefined || v === null ? '' : String(v);
      if (DATE_ONLY_KEYS.test(k)) return <Input type="date" value={str.slice(0, 10)} onChange={(e) => onChange(e.target.value || undefined)} aria-invalid={invalid} disabled={disabled} />;
      if (s.format === 'date-time' || DATETIME_KEYS.test(k)) return <Input type="datetime-local" value={isoToWall(str)} onChange={(e) => onChange(e.target.value ? wallToIso(e.target.value) : undefined)} aria-invalid={invalid} disabled={disabled} />;
      if (TIME_KEYS.test(k) || s.pattern?.includes('[0-5]\\d')) return <Input type="time" value={str} onChange={(e) => onChange(e.target.value || undefined)} aria-invalid={invalid} disabled={disabled} />;
      if (/colou?r$|^(primary|secondary|accent)$/i.test(k)) return (
        <div className="flex items-center gap-2">
          <input type="color" value={str || '#7a4e2d'} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 cursor-pointer rounded-lg border bg-surface p-1" />
          <Input value={str} onChange={(e) => onChange(e.target.value)} className="font-mono" />
        </div>
      );
      if ((s.maxLength ?? 0) > 300 || LONG_TEXT_KEYS.test(k)) return <Textarea value={str} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid} disabled={disabled} dir={/Ar$/.test(k) ? 'rtl' : undefined} />;
      return <Input type={s.format === 'email' ? 'email' : /password/i.test(k) ? 'password' : 'text'} value={str} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid} disabled={disabled} dir={/Ar$/.test(k) ? 'rtl' : undefined} placeholder={s.example !== undefined ? String(s.example) : undefined} />;
    }
  }
}

/** Shows major units (e.g. 250.00 AED) and stores minor units (25000). */
function MoneyInput({ value, onChange, invalid, disabled }: { value: number | undefined; onChange(v: number | undefined): void; invalid?: boolean; disabled?: boolean }) {
  const [text, setText] = useState(value === undefined || value === null ? '' : (value / 100).toString());
  return (
    <div className="relative">
      <Input
        inputMode="decimal"
        value={text}
        aria-invalid={invalid}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value.replace(/,/g, ''));
          onChange(e.target.value === '' || Number.isNaN(n) ? undefined : Math.round(n * 100));
        }}
        className="pe-14 tabular"
      />
      <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-xs font-medium text-muted-foreground">AED</span>
    </div>
  );
}

function ArrayWidget({ k, s, v, onChange, context }: { k: string; s: JsonSchema; v: unknown[] | undefined; onChange(v: unknown): void; context: string }) {
  const item = deref(s.items);
  const list = v ?? [];
  if (item.enum) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {item.enum.map((opt) => {
          const on = list.includes(opt);
          return (
            <button key={String(opt)} type="button" onClick={() => onChange(on ? list.filter((x) => x !== opt) : [...list, opt])} className={cn('rounded-full border px-3 py-1 text-[12.5px] transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted')}>
              {String(opt).replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
    );
  }
  if ((item.type === 'number' || item.type === 'integer') && /days|weekday|restDays|daysOfWeek/i.test(k)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {WEEKDAYS.map((d, i) => {
          const on = list.includes(i);
          return (
            <button key={d} type="button" onClick={() => onChange(on ? list.filter((x) => x !== i) : [...list, i].sort())} className={cn('w-12 rounded-lg border py-1.5 text-[12.5px]', on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted')}>
              {d}
            </button>
          );
        })}
      </div>
    );
  }
  if (item.properties) {
    const rows = list as FormValues[];
    return (
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="relative rounded-xl border bg-muted/20 p-4">
            <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="absolute end-2 top-2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive">
              <Trash2 className="size-4" />
            </button>
            <SchemaForm schema={item} value={row} onChange={(nv) => onChange(rows.map((r, j) => (j === i ? nv : r)))} context={context} />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, initialValues(item)])}>
          <Plus /> Add
        </Button>
      </div>
    );
  }
  return <TagInput value={list as Array<string | number>} onChange={onChange} numeric={item.type === 'number' || item.type === 'integer'} />;
}

function TagInput({ value, onChange, numeric }: { value: Array<string | number>; onChange(v: Array<string | number>): void; numeric?: boolean }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const parts = draft.split(',').map((x) => x.trim()).filter(Boolean);
    if (!parts.length) return;
    onChange([...value, ...parts.map((p) => (numeric ? Number(p) : p))]);
    setDraft('');
  };
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-surface px-2 py-1.5">
      {value.map((t, i) => (
        <Badge key={`${t}-${i}`} tone="primary" className="pe-1">
          {String(t)}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="rounded-full px-1 hover:bg-primary/20">
            ×
          </button>
        </Badge>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={add}
        placeholder={value.length ? '' : 'Type and press Enter'}
        className="min-w-[8rem] flex-1 bg-transparent px-1 text-sm outline-none"
      />
    </div>
  );
}

function NestedObject({ s, v, onChange, context }: { s: JsonSchema; v: FormValues; onChange(v: FormValues): void; context: string }) {
  return (
    <div className="rounded-xl border bg-muted/20 p-4">
      <SchemaForm schema={s} value={v} onChange={onChange} context={context} />
    </div>
  );
}

/** Free-form JSON for open-ended objects (opening hours, audiences, rules). */
function JsonEditor({ value, onChange }: { value: unknown; onChange(v: unknown): void }) {
  const initial = useMemo(() => (value === undefined ? '' : JSON.stringify(value, null, 2)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [text, setText] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  return (
    <div>
      <Textarea
        value={text}
        spellCheck={false}
        className="min-h-[120px] font-mono text-[12.5px]"
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setError(null);
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(e.target.value));
            setError(null);
          } catch {
            setError('Not valid JSON yet');
          }
        }}
        placeholder="{ }"
      />
      {error && <p className="mt-1 text-xs text-warning">{error}</p>}
    </div>
  );
}
